import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TeacherSidebar from './TeacherSidebar';
import TopBar from '../TopBar';
import TeacherSectionDashboard from './TeacherSectionDashboard';
import TeacherSectionCourses from './TeacherSectionCourses';
import TeacherSectionLiveClasses from './TeacherSectionLiveClasses';
import TeacherSectionCourseLibraryManagement from './TeacherSectionCourseLibraryManagement';
import TeacherSectionEarnings from './TeacherSectionEarnings';
import TeacherSectionProfileSettings from './TeacherSectionProfileSettings';
import TeacherSectionMyStudents from './TeacherSectionMyStudents';
import AccountSettings from '../tabs/AccountSettings';
import UserDashboard from '../user/UserDashboard';
import ComingSoonOverlay from '../common/ComingSoonOverlay';
import './TeacherSections.css';
import './TeacherSidebar.css';
import '../user/UserDashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const TeacherDashboard = () => {
  const { t } = useTranslation('teacher');
  const location = useLocation();
  const navigate = useNavigate();
  const initialSection = location.pathname.startsWith('/teacher-dashboard/my-students') ? 'my-students' : 'dashboard';
  const [activeSection, setActiveSection] = useState(initialSection);
  const [sectionKey, setSectionKey] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (location.pathname.startsWith('/teacher-dashboard/my-students')) {
      setActiveSection('my-students');
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarOpen && window.innerWidth <= 768) {
        const sidebar = document.querySelector('.teacher-sidebar');
        const hamburger = document.querySelector('.mobile-menu-toggle');
        if (sidebar && !sidebar.contains(event.target) && hamburger && !hamburger.contains(event.target)) {
          setSidebarOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sidebarOpen]);

  const closeMobileSidebar = () => {
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  
  // Shop functionality state
  const [activeShopTab, setActiveShopTab] = useState('courses');
  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [currentFilters, setCurrentFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [priceFilter, setPriceFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [loading, setLoading] = useState(false);

  // Course Library functionality state
  const [activeCourseLibraryTab, setActiveCourseLibraryTab] = useState('courses');
  const [courseSearch, setCourseSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [priceFilterCourse, setPriceFilterCourse] = useState('');
  const [ratingFilterCourse, setRatingFilterCourse] = useState('');
  const [sortFilter, setSortFilter] = useState('popular');
  const [viewMode, setViewMode] = useState('grid');
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [myLearning, setMyLearning] = useState([]);
  const [courseWishlist, setCourseWishlist] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [courseDetailModal, setCourseDetailModal] = useState({ show: false, course: null });

  // Support Classes functionality state
  const [supportClassModal, setSupportClassModal] = useState({ show: false, type: null });
  const [selectedSchoolLevel, setSelectedSchoolLevel] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [courseTypeImages, setCourseTypeImages] = useState({
    recorded: '/assets/images/recorded_courses.jpg',
    workshops: '/assets/images/workshops.jpg',
    normal: '/assets/images/normal_classes.jpg'
  });

  // Live Learning functionality state (copied from user dashboard)
  const [activeLiveTab, setActiveLiveTab] = useState('book-lesson');
  const [activeBookingTab, setActiveBookingTab] = useState('upcoming');
  const [teacherSearch, setTeacherSearch] = useState('');
  const [specialtyFilter, setSpecialtyFilter] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('');
  const [bookingModal, setBookingModal] = useState({ show: false, teacher: null });
  const [ratingModal, setRatingModal] = useState({ show: false, teacher: null });
  
  // Live Learning data state
  const [teachers, setTeachers] = useState([]);
  const [bookings, setBookings] = useState({
    upcoming: [],
    completed: [],
    cancelled: []
  });
  const [myTeachers, setMyTeachers] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [currentTeacherId, setCurrentTeacherId] = useState(null);
  const [currentTeacherRate, setCurrentTeacherRate] = useState(0);
  const [filteredTeachers, setFilteredTeachers] = useState([]);

  // Free Talk functionality state
  const [activeTalkTab, setActiveTalkTab] = useState('communication-hub');
  const [roomSearch, setRoomSearch] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [levelFilterTalk, setLevelFilterTalk] = useState('');
  const [categoryFilterTalk, setCategoryFilterTalk] = useState('');
  const [rooms, setRooms] = useState([]);
  const [filteredRooms, setFilteredRooms] = useState([]);
  const [myRooms, setMyRooms] = useState([]);
  const [recentRooms, setRecentRooms] = useState([]);
  const [myPosts, setMyPosts] = useState([]);
  const [reportedUsers, setReportedUsers] = useState([]);
  const [roomModal, setRoomModal] = useState({ show: false, room: null });
  const [reportModal, setReportModal] = useState({ show: false, target: null });
  const [currentRoom, setCurrentRoom] = useState(null);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [chatMessages, setChatMessages] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [activeActivityTab, setActiveActivityTab] = useState('my-rooms');
  const [createRoomForm, setCreateRoomForm] = useState({
    title: '',
    language: '',
    type: '',
    category: '',
    level: 'any',
    maxUsers: 10,
    tags: '',
    description: '',
    isPrivate: false
  });
  const [profileSettings, setProfileSettings] = useState({
    username: 'Ms. Smith',
    country: '🇺🇸 United States',
    nativeLanguage: 'English',
    learningLanguage: 'Spanish',
    languageLevel: 'intermediate'
  });
  const [reportForm, setReportForm] = useState({
    reason: '',
    description: ''
  });

  // Current teacher data
  const currentTeacher = {
    id: 'teacher123',
    name: 'Ms. Smith',
    country: '🇺🇸 United States',
    level: 'intermediate',
    avatar: 'MS'
  };

  // API Base URL
  const API_BASE = '/api';
  const DEMO_USER_ID = 1;

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  // Load shop data when shop section is active
  useEffect(() => {
    if (activeSection === 'shop') {
      initializeShop();
    }
  }, [activeSection]);

  // Initialize shop data
  const initializeShop = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadCategories(),
        loadCart(),
        loadWishlist()
      ]);
    } catch (error) {
      console.error('Error initializing shop:', error);
      showNotification('Failed to load shop data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load categories from API
  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/categories`);
      const categories = await response.json();
      setCategories(categories);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  // Load cart from API
  const loadCart = async () => {
    try {
      const response = await fetch(`${API_BASE}/cart?userId=${DEMO_USER_ID}`);
      const data = await response.json();
      setCart(data.items || []);
    } catch (error) {
      console.error('Error loading cart:', error);
    }
  };

  // Load wishlist from API
  const loadWishlist = async () => {
    try {
      const response = await fetch(`${API_BASE}/wishlist?userId=${DEMO_USER_ID}`);
      const data = await response.json();
      setWishlist(data.items || []);
    } catch (error) {
      console.error('Error loading wishlist:', error);
    }
  };

  // Load products from API
  const loadProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        category: currentCategory,
        page: currentPage,
        limit: 12,
        ...currentFilters
      });

      const response = await fetch(`${API_BASE}/products?${params}`);
      const data = await response.json();
      
      setFilteredProducts(data.products || []);
      setTotalPages(data.pagination?.pages || 1);
    } catch (error) {
      console.error('Error loading products:', error);
      showNotification('Failed to load products', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Add item to cart
  const addToCart = async (productId, quantity = 1) => {
    try {
      const response = await fetch(`${API_BASE}/cart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          productId,
          quantity,
          userId: DEMO_USER_ID
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        await loadCart();
        showNotification('Item added to cart!', 'success');
      } else {
        showNotification(result.error || 'Failed to add item to cart', 'error');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      showNotification('Failed to add item to cart', 'error');
    }
  };

  // Update cart item quantity
  const updateCartQuantity = async (productId, quantity) => {
    try {
      const response = await fetch(`${API_BASE}/cart/${productId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          quantity,
          userId: DEMO_USER_ID
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        await loadCart();
      }
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  };

  // Remove item from cart
  const removeFromCart = async (productId) => {
    try {
      const response = await fetch(`${API_BASE}/cart/${productId}?userId=${DEMO_USER_ID}`, {
        method: 'DELETE'
      });
      
      const result = await response.json();
      
      if (result.success) {
        await loadCart();
        showNotification('Item removed from cart', 'success');
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
    }
  };

  // Toggle wishlist item
  const toggleWishlistItem = async (productId) => {
    try {
      const isInWishlist = wishlist.some(item => item.product_id === productId);
      
      if (isInWishlist) {
        // Remove from wishlist
        const response = await fetch(`${API_BASE}/wishlist/${productId}?userId=${DEMO_USER_ID}`, {
          method: 'DELETE'
        });
        
        const result = await response.json();
        if (result.success) {
          await loadWishlist();
          showNotification('Removed from wishlist', 'success');
        }
      } else {
        // Add to wishlist
        const response = await fetch(`${API_BASE}/wishlist`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            productId,
            userId: DEMO_USER_ID
          })
        });
        
        const result = await response.json();
        if (result.success) {
          await loadWishlist();
          showNotification('Added to wishlist!', 'success');
        }
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
    }
  };

  // Handle search
  const handleSearch = async () => {
    setCurrentFilters(prev => ({ ...prev, search: searchTerm }));
    setCurrentPage(1);
    await loadProducts();
  };

  // Handle filter changes
  const handleFilterChange = async () => {
    const newFilters = { ...currentFilters };
    
    if (priceFilter) {
      const [min, max] = priceFilter.split('-');
      newFilters.minPrice = min;
      newFilters.maxPrice = max === '+' ? null : max;
    } else {
      delete newFilters.minPrice;
      delete newFilters.maxPrice;
    }
    
    setCurrentFilters(newFilters);
    setCurrentPage(1);
    await loadProducts();
  };

  // Handle sort changes
  const handleSortChange = async () => {
    const newFilters = { ...currentFilters };
    
    switch (sortBy) {
      case 'price-low':
        newFilters.sortBy = 'price';
        newFilters.sortOrder = 'ASC';
        break;
      case 'price-high':
        newFilters.sortBy = 'price';
        newFilters.sortOrder = 'DESC';
        break;
      case 'rating':
        newFilters.sortBy = 'rating';
        newFilters.sortOrder = 'DESC';
        break;
      case 'newest':
        newFilters.sortBy = 'created_at';
        newFilters.sortOrder = 'DESC';
        break;
      default:
        newFilters.sortBy = 'name';
        newFilters.sortOrder = 'ASC';
    }
    
    setCurrentFilters(newFilters);
    setCurrentPage(1);
    await loadProducts();
  };

  // Change page
  const changePage = async (page) => {
    if (page < 1 || page > totalPages) return;
    
    setCurrentPage(page);
    await loadProducts();
  };

  // Generate stars for ratings
  const generateStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<i key={i} className="material-icons">star</i>);
    }
    
    if (hasHalfStar) {
      stars.push(<i key="half" className="material-icons">star_half</i>);
    }
    
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<i key={`empty-${i}`} className="material-icons">star_border</i>);
    }
    
    return stars;
  };

  // Get cart total
  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  // Get cart count
  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  // Course Library functions
  const loadCourseLibraryData = async () => {
    try {
      setLoading(true);
      // Mock data for demonstration
      const mockCourses = [
        {
          id: 1,
          title: 'Advanced English Grammar',
          category: 'grammar',
          level: 'advanced',
          price: 49.99,
          originalPrice: 79.99,
          duration: '12 hours',
          students: 1247,
          rating: 4.8,
          reviewCount: 156,
          description: 'Comprehensive grammar course covering advanced concepts and practical applications.',
          featured: true,
          teacher: {
            name: 'Dr. Sarah Johnson',
            avatar: 'SJ',
            country: '🇺🇸 USA'
          },
          image: '/course-1.jpg'
        },
        {
          id: 2,
          title: 'Business Communication Skills',
          category: 'business',
          level: 'intermediate',
          price: 39.99,
          originalPrice: 59.99,
          duration: '8 hours',
          students: 892,
          rating: 4.6,
          reviewCount: 89,
          description: 'Essential communication skills for professional environments.',
          featured: false,
          teacher: {
            name: 'Prof. Michael Chen',
            avatar: 'MC',
            country: '🇨🇦 Canada'
          },
          image: '/course-2.jpg'
        },
        {
          id: 3,
          title: 'IELTS Preparation Masterclass',
          category: 'ielts',
          level: 'advanced',
          price: 79.99,
          originalPrice: 99.99,
          duration: '20 hours',
          students: 2156,
          rating: 4.9,
          reviewCount: 234,
          description: 'Complete IELTS preparation with practice tests and strategies.',
          featured: true,
          teacher: {
            name: 'Emma Rodriguez',
            avatar: 'ER',
            country: '🇪🇸 Spain'
          },
          image: '/course-3.jpg'
        },
        {
          id: 4,
          title: 'Speaking Confidence Builder',
          category: 'speaking',
          level: 'beginner',
          price: 29.99,
          originalPrice: 49.99,
          duration: '6 hours',
          students: 567,
          rating: 4.7,
          reviewCount: 78,
          description: 'Build confidence in speaking English through interactive exercises.',
          featured: false,
          teacher: {
            name: 'Lisa Thompson',
            avatar: 'LT',
            country: '🇬🇧 UK'
          },
          image: '/course-4.jpg'
        },
        {
          id: 5,
          title: 'Academic Writing Skills',
          category: 'writing',
          level: 'intermediate',
          price: 59.99,
          originalPrice: 79.99,
          duration: '10 hours',
          students: 734,
          rating: 4.5,
          reviewCount: 112,
          description: 'Master academic writing techniques and essay structure.',
          featured: false,
          teacher: {
            name: 'David Wilson',
            avatar: 'DW',
            country: '🇦🇺 Australia'
          },
          image: '/course-5.jpg'
        },
        {
          id: 6,
          title: 'Vocabulary Enhancement',
          category: 'vocabulary',
          level: 'intermediate',
          price: 34.99,
          originalPrice: 54.99,
          duration: '8 hours',
          students: 445,
          rating: 4.4,
          reviewCount: 67,
          description: 'Expand your vocabulary with practical exercises and context.',
          featured: false,
          teacher: {
            name: 'Maria Garcia',
            avatar: 'MG',
            country: '🇲🇽 Mexico'
          },
          image: '/course-6.jpg'
        }
      ];

      const mockResources = [
        {
          id: 1,
          title: 'Grammar Worksheets Collection',
          type: 'Worksheet',
          category: 'Language',
          downloads: 456,
          rating: 4.7,
          lastUpdated: '2024-01-15'
        },
        {
          id: 2,
          title: 'Business English Templates',
          type: 'Template',
          category: 'Business',
          downloads: 234,
          rating: 4.5,
          lastUpdated: '2024-01-10'
        }
      ];

      const mockTemplates = [
        {
          id: 1,
          title: 'Beginner English Course Template',
          category: 'Language',
          duration: '8 weeks',
          lessons: 24,
          rating: 4.6,
          downloads: 189
        },
        {
          id: 2,
          title: 'TOEFL Preparation Template',
          category: 'Exam Prep',
          duration: '12 weeks',
          lessons: 36,
          rating: 4.8,
          downloads: 267
        }
      ];

      const mockMaterials = [
        {
          id: 1,
          title: 'Interactive Vocabulary Games',
          type: 'Game',
          category: 'Language',
          ageGroup: 'Teenagers',
          rating: 4.4,
          downloads: 123
        },
        {
          id: 2,
          title: 'Pronunciation Practice Audio',
          type: 'Audio',
          category: 'Language',
          ageGroup: 'Adults',
          rating: 4.6,
          downloads: 89
        }
      ];

      setCourses(mockCourses);
      setFilteredCourses(mockCourses);

      // Mock my learning data
      const mockMyLearning = [
        {
          id: 1,
          title: "Advanced English Grammar",
          instructor: "Dr. Sarah Johnson",
          progress: 75,
          completedLessons: 18,
          totalLessons: 24,
          lastAccessed: "2024-01-15",
          nextLesson: "Conditional Sentences"
        },
        {
          id: 2,
          title: "Business Communication Skills",
          instructor: "Prof. Michael Chen",
          progress: 45,
          completedLessons: 9,
          totalLessons: 20,
          lastAccessed: "2024-01-14",
          nextLesson: "Email Writing"
        }
      ];

      setMyLearning(mockMyLearning);

      // Mock wishlist data
      const mockWishlist = [
        {
          id: 3,
          title: "IELTS Preparation Masterclass",
          instructor: "Emma Rodriguez",
          rating: 4.9,
          reviewCount: 234,
          price: 79.99,
          originalPrice: 99.99
        },
        {
          id: 5,
          title: "Academic Writing Skills",
          instructor: "David Wilson",
          rating: 4.5,
          reviewCount: 112,
          price: 59.99,
          originalPrice: 79.99
        }
      ];

      setCourseWishlist(mockWishlist);

      // Mock certificates data
      const mockCertificates = [
        {
          id: 1,
          title: "Speaking Confidence Builder",
          instructor: "Lisa Thompson",
          grade: "A+",
          hours: 40,
          completedDate: "2024-01-10"
        },
        {
          id: 2,
          title: "Vocabulary Enhancement",
          instructor: "Maria Garcia",
          grade: "A",
          hours: 25,
          completedDate: "2024-01-05"
        }
      ];

      setCertificates(mockCertificates);
    } catch (error) {
      console.error('Error loading course library data:', error);
      showNotification('Failed to load course library data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filterCourses = () => {
    let filtered = [...courses];

    // Search filter
    if (courseSearch) {
      filtered = filtered.filter(course =>
        course.title.toLowerCase().includes(courseSearch.toLowerCase()) ||
        course.teacher.name.toLowerCase().includes(courseSearch.toLowerCase())
      );
    }

    // Category filter
    if (categoryFilter) {
      filtered = filtered.filter(course => course.category === categoryFilter);
    }

    // Level filter
    if (levelFilter) {
      filtered = filtered.filter(course => course.level === levelFilter);
    }

    // Price filter
    if (priceFilterCourse) {
      switch (priceFilterCourse) {
        case 'free':
          filtered = filtered.filter(course => course.price === 0);
          break;
        case 'under50':
          filtered = filtered.filter(course => course.price < 50);
          break;
        case 'under100':
          filtered = filtered.filter(course => course.price < 100);
          break;
        default:
          break;
      }
    }

    // Rating filter
    if (ratingFilterCourse) {
      filtered = filtered.filter(course => course.rating >= parseFloat(ratingFilterCourse));
    }

    // Sort filter
    let sorted = [...filtered];
    switch (sortFilter) {
      case 'newest':
        sorted.sort((a, b) => b.id - a.id);
        break;
      case 'rating':
        sorted.sort((a, b) => b.rating - a.rating);
        break;
      case 'price-low':
        sorted.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        sorted.sort((a, b) => b.price - a.price);
        break;
      default: // popular
        sorted.sort((a, b) => b.students - a.students);
    }

    setFilteredCourses(sorted);
  };

  const openCourseDetailModal = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    setCourseDetailModal({ show: true, course });
  };

  const closeCourseDetailModal = () => {
    setCourseDetailModal({ show: false, course: null });
  };

  const enrollCourse = async (courseId) => {
    try {
      // Mock enrollment functionality
      showNotification('Successfully enrolled in course!', 'success');
    } catch (error) {
      console.error('Error enrolling in course:', error);
      showNotification('Failed to enroll in course', 'error');
    }
  };

  const addToCourseWishlist = async (courseId) => {
    try {
      const course = courses.find(c => c.id === courseId);
      if (course && !courseWishlist.find(c => c.id === courseId)) {
        setCourseWishlist([...courseWishlist, course]);
        showNotification('Added to wishlist!', 'success');
      }
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      showNotification('Failed to add to wishlist', 'error');
    }
  };

  const removeFromCourseWishlist = async (courseId) => {
    try {
      setCourseWishlist(courseWishlist.filter(c => c.id !== courseId));
      showNotification('Removed from wishlist!', 'success');
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      showNotification('Failed to remove from wishlist', 'error');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Support Classes functions
  const openSupportClassModal = (type) => {
    setSupportClassModal({ show: true, type });
    setSelectedSchoolLevel('');
    setSelectedSubject('');
    setSelectedYear('');
  };

  const closeSupportClassModal = () => {
    setSupportClassModal({ show: false, type: null });
    setSelectedSchoolLevel('');
    setSelectedSubject('');
    setSelectedYear('');
  };

  const getSchoolLevels = () => {
    return [
      { value: 'primary', label: 'Primary School', years: ['Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6'] },
      { value: 'middle', label: 'Middle School', years: ['Grade 1', 'Grade 2', 'Grade 3', 'Grade 4'] },
      { value: 'high', label: 'High School', years: ['Grade 1', 'Grade 2', 'Grade 3'] }
    ];
  };

  const getSubjectsByLevel = (level) => {
    const subjects = {
      primary: ['English', 'Math', 'Science', 'Social Studies', 'Arabic'],
      middle: ['English', 'Math', 'Physics', 'Chemistry', 'Biology', 'Arabic', 'Islamic Studies'],
      high: ['English', 'Math', 'Physics', 'Chemistry', 'Biology', 'Arabic', 'Islamic Studies', 'Computer Science']
    };
    return subjects[level] || [];
  };

  const handleSupportClassSubmit = () => {
    if (!selectedSchoolLevel || !selectedSubject || !selectedYear) {
      showNotification('Please select school level, subject, and year', 'error');
      return;
    }

    const supportClassData = {
      type: supportClassModal.type,
      schoolLevel: selectedSchoolLevel,
      subject: selectedSubject,
      year: selectedYear
    };

    showNotification(`Starting ${supportClassModal.type} for ${selectedSubject} - ${selectedYear}`, 'success');
    closeSupportClassModal();
    
    // Here you would typically navigate to the full learning screen
    console.log('Support Class Data:', supportClassData);
  };

  // Free Talk functions
  const loadFreeTalkData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadRooms(),
        loadMyActivity()
      ]);
    } catch (error) {
      console.error('Error loading free talk data:', error);
      showNotification('Failed to load free talk data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadRooms = async () => {
    // Mock data for rooms
    const mockRooms = [
      {
        id: 'room1',
        title: 'Advanced English Conversation',
        language: 'English',
        type: 'voice',
        level: 'advanced',
        category: 'conversation',
        users: 8,
        maxUsers: 15,
        creator: { name: 'John Doe', avatar: 'JD', country: '🇬🇧 UK' },
        tags: ['#Advanced', '#Conversation', '#English'],
        isPrivate: false,
        isActive: true
      },
      {
        id: 'room2',
        title: 'IELTS Speaking Practice',
        language: 'English',
        type: 'both',
        level: 'intermediate',
        category: 'ielts',
        users: 12,
        maxUsers: 20,
        creator: { name: 'Sarah Wilson', avatar: 'SW', country: '🇦🇺 Australia' },
        tags: ['#IELTS', '#Speaking', '#Practice'],
        isPrivate: false,
        isActive: true
      },
      {
        id: 'room3',
        title: 'Business English Networking',
        language: 'English',
        type: 'voice',
        level: 'advanced',
        category: 'business',
        users: 6,
        maxUsers: 12,
        creator: { name: 'Mike Chen', avatar: 'MC', country: '🇺🇸 USA' },
        tags: ['#Business', '#Networking', '#Professional'],
        isPrivate: true,
        isActive: false
      },
      {
        id: 'room4',
        title: 'Spanish Grammar Practice',
        language: 'Spanish',
        type: 'text',
        level: 'beginner',
        category: 'grammar',
        users: 15,
        maxUsers: 25,
        creator: { name: 'Maria Garcia', avatar: 'MG', country: '🇪🇸 Spain' },
        tags: ['#Spanish', '#Grammar', '#Beginner'],
        isPrivate: false,
        isActive: true
      }
    ];
    
    setRooms(mockRooms);
    setFilteredRooms(mockRooms);
  };

  const loadMyActivity = async () => {
    // Mock data for my activity
    const mockMyRooms = [
      {
        id: 'myroom1',
        title: 'Teacher English Practice',
        language: 'English',
        type: 'both',
        level: 'advanced',
        category: 'conversation',
        users: 3,
        maxUsers: 8,
        isActive: true
      },
      {
        id: 'myroom2',
        title: 'Spanish Learning Group',
        language: 'Spanish',
        type: 'voice',
        level: 'intermediate',
        category: 'conversation',
        users: 5,
        maxUsers: 10,
        isActive: false
      }
    ];
    
    const mockRecentRooms = [
      {
        id: 'recent1',
        title: 'Advanced English Conversation',
        joinedAt: '2024-01-15',
        duration: '2h 30m'
      },
      {
        id: 'recent2',
        title: 'Spanish Grammar Practice',
        joinedAt: '2024-01-14',
        duration: '1h 45m'
      }
    ];
    
    const mockMyPosts = [
      {
        id: 'post1',
        content: 'Great practice session today!',
        room: 'Advanced English Conversation',
        time: '2 hours ago'
      },
      {
        id: 'post2',
        content: 'Anyone up for grammar practice?',
        room: 'Spanish Grammar Practice',
        time: '1 day ago'
      }
    ];
    
    const mockReportedUsers = [
      {
        id: 'user1',
        name: 'SpamUser123',
        reason: 'Inappropriate behavior',
        reportedAt: '2024-01-10'
      }
    ];
    
    setMyRooms(mockMyRooms);
    setRecentRooms(mockRecentRooms);
    setMyPosts(mockMyPosts);
    setReportedUsers(mockReportedUsers);
  };

  const filterRooms = () => {
    let filtered = [...rooms];

    if (roomSearch) {
      filtered = filtered.filter(room => 
        room.title.toLowerCase().includes(roomSearch.toLowerCase()) ||
        room.tags.some(tag => tag.toLowerCase().includes(roomSearch.toLowerCase()))
      );
    }

    if (languageFilter) {
      filtered = filtered.filter(room => room.language.toLowerCase() === languageFilter.toLowerCase());
    }

    if (typeFilter) {
      filtered = filtered.filter(room => room.type === typeFilter);
    }

    if (levelFilterTalk) {
      filtered = filtered.filter(room => room.level === levelFilterTalk);
    }

    if (categoryFilterTalk) {
      filtered = filtered.filter(room => room.category === categoryFilterTalk);
    }

    setFilteredRooms(filtered);
  };

  const joinRoom = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    if (room) {
      setRoomModal({ show: true, room: room });
      setCurrentRoom(room);
      
      // Mock participants
      setParticipants([
        { id: 'p1', name: 'John Doe', country: '🇬🇧 UK', isMuted: false, isOwner: true },
        { id: 'p2', name: 'Sarah Wilson', country: '🇦🇺 Australia', isMuted: false, isOwner: false },
        { id: 'p3', name: 'Mike Chen', country: '🇺🇸 USA', isMuted: true, isOwner: false },
        { id: 'p4', name: 'Ms. Smith', country: '🇺🇸 USA', isMuted: false, isOwner: false }
      ]);
      
      // Mock chat messages
      setChatMessages([
        { id: 'm1', author: 'John Doe', content: 'Welcome everyone!', time: '14:30', isOwn: false },
        { id: 'm2', author: 'Sarah Wilson', content: 'Thanks for having us!', time: '14:31', isOwn: false },
        { id: 'm3', author: 'Ms. Smith', content: 'Hello everyone! I\'m here to practice too.', time: '14:32', isOwn: true }
      ]);
    }
  };

  const leaveRoom = () => {
    setRoomModal({ show: false, room: null });
    setCurrentRoom(null);
    setParticipants([]);
    setChatMessages([]);
  };

  const toggleMic = () => {
    setIsMicOn(!isMicOn);
  };

  const toggleSpeaker = () => {
    setIsSpeakerOn(!isSpeakerOn);
  };

  const shareScreen = () => {
    showNotification('Screen sharing started', 'success');
  };

  const sendMessage = () => {
    if (!newMessage.trim()) return;
    
    const newMessageObj = {
      id: `msg${Date.now()}`,
      author: currentTeacher.name,
      content: newMessage,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      isOwn: true
    };
    
    setChatMessages(prev => [...prev, newMessageObj]);
    setNewMessage('');
  };

  const handleMessageKeyPress = (e) => {
    if (e.key === 'Enter') {
      sendMessage();
    }
  };

  const openReportModal = (target) => {
    setReportModal({ show: true, target: target });
  };

  const closeReportModal = () => {
    setReportModal({ show: false, target: null });
  };

  const reportRoom = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    openReportModal({ type: 'room', id: roomId, name: room?.title });
  };

  const reportUser = (userId) => {
    const user = participants.find(p => p.id === userId);
    openReportModal({ type: 'user', id: userId, name: user?.name });
  };

  const submitReport = (reportData) => {
    showNotification('Report submitted successfully', 'success');
    closeReportModal();
  };

  const createRoom = (roomData) => {
    const newRoom = {
      id: `room${Date.now()}`,
      ...roomData,
      creator: currentTeacher,
      users: 0,
      isActive: false
    };
    
    setRooms(prev => [newRoom, ...prev]);
    showNotification('Room created successfully!', 'success');
  };

  const handleCreateRoomSubmit = (e) => {
    e.preventDefault();
    createRoom(createRoomForm);
    setCreateRoomForm({
      title: '',
      language: '',
      type: '',
      category: '',
      level: 'any',
      maxUsers: 10,
      tags: '',
      description: '',
      isPrivate: false
    });
  };

  const handleProfileSettingsSubmit = (e) => {
    e.preventDefault();
    showNotification('Settings saved successfully!', 'success');
  };

  const handleReportSubmit = (e) => {
    e.preventDefault();
    submitReport(reportForm);
  };

  const deleteRoom = (roomId) => {
    showNotification('Room deleted successfully', 'success');
  };

  const editRoom = (roomId) => {
    showNotification('Room edit feature coming soon', 'info');
  };

  // Update filters and re-filter
  useEffect(() => {
    filterCourses();
  }, [courseSearch, categoryFilter, levelFilter, priceFilterCourse, ratingFilterCourse, sortFilter, courses]);

  // Load Course Library data when section is active
  useEffect(() => {
    if (activeSection === 'course-library') {
      loadCourseLibraryData();
    }
  }, [activeSection]);

  // Load Free Talk data when section is active
  useEffect(() => {
    if (activeSection === 'free-talk') {
      loadFreeTalkData();
    }
  }, [activeSection]);

  // Filter rooms when filters change
  useEffect(() => {
    if (activeSection === 'free-talk' && activeTalkTab === 'communication-hub') {
      filterRooms();
    }
  }, [roomSearch, languageFilter, typeFilter, levelFilterTalk, categoryFilterTalk, rooms]);

  // Load live learning data when live sessions section is active
  useEffect(() => {
    if (activeSection === 'live-sessions') {
      initializeLiveLearning();
    }
  }, [activeSection]);

  // Filter teachers when search or filters change
  useEffect(() => {
    if (activeSection === 'live-sessions' && activeLiveTab === 'book-lesson') {
      filterTeachers();
    }
  }, [teacherSearch, specialtyFilter, ratingFilter, availabilityFilter, teachers]);

  // Load course type images on component mount
  useEffect(() => {
    loadCourseTypeImages();
  }, []);

  const loadCourseTypeImages = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/course-type-images/course-type-images`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.images) {
          setCourseTypeImages(prev => ({
            ...prev,
            ...data.images
          }));
        }
      }
    } catch (error) {
      console.error('Error loading course type images:', error);
    }
  };

  // Initialize Live Learning
  const initializeLiveLearning = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadTeachers(),
        loadBookings(),
        loadMyTeachers(),
        loadRatings()
      ]);
    } catch (error) {
      console.error('Error initializing live learning:', error);
      showNotification('Failed to load live learning data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load teachers from API
  const loadTeachers = async () => {
    try {
      const response = await fetch(`${API_BASE}/teachers`);
      const data = await response.json();
      setTeachers(data.teachers || []);
      setFilteredTeachers(data.teachers || []);
    } catch (error) {
      console.error('Error loading teachers:', error);
      // Mock data for demo
      const mockTeachers = [
        {
          id: 1,
          name: 'Sarah Johnson',
          specialty: 'English',
          rating: 4.8,
          totalStudents: 156,
          bio: 'Experienced English teacher with 8 years of teaching experience. Specializes in IELTS and TOEFL preparation.',
          hourlyRate: 25,
          availability: 'Available Today',
          image: '/placeholder-avatar.jpg'
        },
        {
          id: 2,
          name: 'Maria Garcia',
          specialty: 'Spanish',
          rating: 4.9,
          totalStudents: 203,
          bio: 'Native Spanish speaker with a passion for teaching. Great for beginners and conversation practice.',
          hourlyRate: 20,
          availability: 'Available Tomorrow',
          image: '/placeholder-avatar.jpg'
        },
        {
          id: 3,
          name: 'Pierre Dubois',
          specialty: 'French',
          rating: 4.7,
          totalStudents: 89,
          bio: 'French native speaker with expertise in business French and cultural immersion.',
          hourlyRate: 30,
          availability: 'Available This Week',
          image: '/placeholder-avatar.jpg'
        }
      ];
      setTeachers(mockTeachers);
      setFilteredTeachers(mockTeachers);
    }
  };

  // Load bookings from API
  const loadBookings = async () => {
    try {
      const response = await fetch(`${API_BASE}/bookings?userId=${DEMO_USER_ID}`);
      const data = await response.json();
      setBookings(data.bookings || {
        upcoming: [],
        completed: [],
        cancelled: []
      });
    } catch (error) {
      console.error('Error loading bookings:', error);
      // Mock data for demo
      setBookings({
        upcoming: [
          {
            id: 1,
            teacherName: 'Sarah Johnson',
            teacherImage: '/placeholder-avatar.jpg',
            subject: 'English Conversation',
            date: '2024-01-15',
            time: '14:00',
            duration: 60
          },
          {
            id: 2,
            teacherName: 'Maria Garcia',
            teacherImage: '/placeholder-avatar.jpg',
            subject: 'Spanish Grammar',
            date: '2024-01-17',
            time: '16:30',
            duration: 45
          }
        ],
        completed: [
          {
            id: 3,
            teacherName: 'Pierre Dubois',
            teacherImage: '/placeholder-avatar.jpg',
            subject: 'French Basics',
            date: '2024-01-10',
            time: '10:00',
            duration: 60
          }
        ],
        cancelled: []
      });
    }
  };

  // Load my teachers from API
  const loadMyTeachers = async () => {
    try {
      const response = await fetch(`${API_BASE}/my-teachers?userId=${DEMO_USER_ID}`);
      const data = await response.json();
      setMyTeachers(data.teachers || []);
    } catch (error) {
      console.error('Error loading my teachers:', error);
      // Mock data for demo
      setMyTeachers([
        {
          id: 1,
          name: 'Sarah Johnson',
          specialty: 'English',
          rating: 4.8,
          lessonsCompleted: 12,
          lastLesson: '2024-01-10',
          image: '/placeholder-avatar.jpg'
        }
      ]);
    }
  };

  // Load ratings from API
  const loadRatings = async () => {
    try {
      const response = await fetch(`${API_BASE}/ratings?userId=${DEMO_USER_ID}`);
      const data = await response.json();
      setRatings(data.ratings || []);
    } catch (error) {
      console.error('Error loading ratings:', error);
      // Mock data for demo
      setRatings([
        {
          id: 1,
          teacherName: 'Pierre Dubois',
          subject: 'French Basics',
          date: '2024-01-10',
          canRate: true
        }
      ]);
    }
  };

  // Filter teachers based on search and filters
  const filterTeachers = () => {
    let filtered = teachers.filter(teacher => {
      const matchesSearch = teacher.name.toLowerCase().includes(teacherSearch.toLowerCase()) ||
                           teacher.specialty.toLowerCase().includes(teacherSearch.toLowerCase());
      const matchesSpecialty = !specialtyFilter || teacher.specialty.toLowerCase() === specialtyFilter.toLowerCase();
      const matchesRating = !ratingFilter || teacher.rating >= parseFloat(ratingFilter);
      const matchesAvailability = !availabilityFilter || 
        (availabilityFilter === 'today' && teacher.availability.includes('Today')) ||
        (availabilityFilter === 'tomorrow' && teacher.availability.includes('Tomorrow')) ||
        (availabilityFilter === 'week' && teacher.availability.includes('Week'));

      return matchesSearch && matchesSpecialty && matchesRating && matchesAvailability;
    });

    setFilteredTeachers(filtered);
  };

  // Join lesson
  const joinLesson = (bookingId) => {
    showNotification('Joining lesson...', 'success');
    // Implementation for joining lesson
  };

  // Reschedule booking
  const rescheduleBooking = (bookingId) => {
    showNotification('Opening reschedule dialog...', 'success');
    // Implementation for rescheduling
  };

  // Cancel booking
  const cancelBooking = (bookingId) => {
    if (window.confirm('Are you sure you want to cancel this booking?')) {
      showNotification('Booking cancelled', 'success');
      // Implementation for cancelling booking
    }
  };

  // Rate lesson
  const rateLesson = (bookingId) => {
    setRatingModal({ show: true, teacher: { id: bookingId } });
  };

  // View teacher profile
  const viewTeacherProfile = (teacherId) => {
    showNotification('Opening teacher profile...', 'success');
    // Implementation for viewing teacher profile
  };

  // Calculate booking amount
  const calculateBookingAmount = (duration, hourlyRate) => {
    return (duration / 60) * hourlyRate;
  };

  // Update booking counts
  const updateBookingCounts = () => {
    // Implementation for updating booking counts
  };

  // Open booking modal
  const openBookingModal = (teacherId) => {
    const teacher = teachers.find(t => t.id === teacherId);
    setBookingModal({ show: true, teacher });
  };

  // Close booking modal
  const closeBookingModal = () => {
    setBookingModal({ show: false, teacher: null });
  };

  // Open rating modal
  const openRatingModal = (teacherId) => {
    const teacher = teachers.find(t => t.id === teacherId);
    setRatingModal({ show: true, teacher });
  };

  // Close rating modal
  const closeRatingModal = () => {
    setRatingModal({ show: false, teacher: null });
  };

  // Handle booking submit
  const handleBookingSubmit = async (bookingData) => {
    try {
      const response = await fetch(`${API_BASE}/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...bookingData,
          userId: DEMO_USER_ID
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        await loadBookings();
        closeBookingModal();
        showNotification('Booking created successfully!', 'success');
      } else {
        showNotification(result.error || 'Failed to create booking', 'error');
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      showNotification('Failed to create booking', 'error');
    }
  };

  // Handle rating submit
  const handleRatingSubmit = async (ratingData) => {
    try {
      const response = await fetch(`${API_BASE}/ratings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...ratingData,
          userId: DEMO_USER_ID
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        await loadRatings();
        closeRatingModal();
        showNotification('Rating submitted successfully!', 'success');
      } else {
        showNotification(result.error || 'Failed to submit rating', 'error');
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      showNotification('Failed to submit rating', 'error');
    }
  };

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return <TeacherSectionDashboard />;
      case 'my-students':
        return <TeacherSectionMyStudents key={`my-students-${sectionKey}`} />;
      case 'courses':
        return <TeacherSectionCourses setActiveSection={setActiveSection} />;
      case 'add-course':
        return <TeacherSectionCourses setActiveSection={setActiveSection} />;
      case 'live-classes':
        return <TeacherSectionLiveClasses />;
      case 'course-library-management':
        return <TeacherSectionCourseLibraryManagement />;
      case 'earnings':
        return <TeacherSectionEarnings />;
      case 'add-article':
        return (
          <div className="teacher-main-content">
            <div className="teacher-content-header">
              <h1 className="teacher-content-title">{t('articles.addArticle.title')}</h1>
              <p className="teacher-content-subtitle">{t('articles.addArticle.subtitle')}</p>
            </div>
            <div className="teacher-section">
              <div className="teacher-section-header">
                <div className="teacher-section-title">
                  <i className="material-icons">edit</i>
                  {t('articles.addArticle.articleCreation')}
                </div>
              </div>
              <p>{t('articles.addArticle.comingSoon')}</p>
            </div>
          </div>
        );
      case 'my-articles':
        return (
          <div className="teacher-main-content">
            <div className="teacher-content-header">
              <h1 className="teacher-content-title">{t('articles.myArticles.title')}</h1>
              <p className="teacher-content-subtitle">{t('articles.myArticles.subtitle')}</p>
            </div>
            <div className="teacher-section">
              <div className="teacher-section-header">
                <div className="teacher-section-title">
                  <i className="material-icons">article</i>
                  {t('articles.myArticles.articleManagement')}
                </div>
              </div>
              <p>{t('articles.myArticles.comingSoon')}</p>
            </div>
          </div>
        );
      case 'vocabulary':
        return (
          <div className="teacher-main-content">
            <div className="teacher-content-header">
              <h1 className="teacher-content-title">{t('vocabulary.title')}</h1>
              <p className="teacher-content-subtitle">{t('vocabulary.subtitle')}</p>
            </div>
            <div className="teacher-section" style={{ position: 'relative' }}>
              <ComingSoonOverlay onClose={() => setActiveSection('dashboard')} className="higher-position" />
              <div className="teacher-section-header">
                <div className="teacher-section-title">
                  <i className="material-icons">library_books</i>
                  {t('vocabulary.vocabularyManagement')}
                </div>
              </div>
              <p>{t('vocabulary.comingSoon')}</p>
            </div>
          </div>
        );
      case 'student-chat':
        return (
          <div className="teacher-main-content">
            <div className="teacher-content-header">
              <h1 className="teacher-content-title">{t('studentChat.title')}</h1>
              <p className="teacher-content-subtitle">{t('studentChat.subtitle')}</p>
            </div>
            <div className="teacher-section" style={{ position: 'relative' }}>
              <ComingSoonOverlay onClose={() => setActiveSection('dashboard')} className="higher-position" />
              <div className="teacher-section-header">
                <div className="teacher-section-title">
                  <i className="material-icons">chat</i>
                  {t('studentChat.studentCommunication')}
                </div>
              </div>
              <p>{t('studentChat.comingSoon')}</p>
            </div>
          </div>
        );
      case 'notifications':
        return (
          <div className="teacher-main-content">
            <div className="teacher-content-header">
              <h1 className="teacher-content-title">{t('notifications.title')}</h1>
              <p className="teacher-content-subtitle">{t('notifications.subtitle')}</p>
            </div>
            <div className="teacher-section">
              <div className="teacher-section-header">
                <div className="teacher-section-title">
                  <i className="material-icons">notifications</i>
                  {t('notifications.notificationCenter')}
                </div>
              </div>
              <p>{t('notifications.comingSoon')}</p>
            </div>
          </div>
        );
      case 'profile-settings':
        return <TeacherSectionProfileSettings key={`profile-settings-${sectionKey}`} />;
      case 'account-settings':
        return (
          <div className="teacher-main-content" key={`account-settings-wrapper-${sectionKey}`}>
            <div className="teacher-content-header">
              <h1 className="teacher-content-title">{t('sidebar.accountSettings')}</h1>
              <p className="teacher-content-subtitle">{t('accountSettings.subtitle', { defaultValue: 'Manage your account security and preferences' })}</p>
            </div>
            <AccountSettings key={`account-settings-${sectionKey}`} />
          </div>
        );
      case 'subscription':
        return (
          <div className="teacher-learning-content" key={`subscription-${sectionKey}`}>
            <UserDashboard
              initialSection="subscription"
              hideSidebar={true}
            />
          </div>
        );
      case 'live-sessions':
        return (
          <div className="user-main-content">
            <div className="user-content-header">
              <h1 className="user-content-title">Live Learning</h1>
              <p className="user-content-subtitle">Book lessons with native speakers and track your progress</p>
            </div>

            {/* Live Learning Navigation Tabs */}
            <div className="user-section" style={{ position: 'relative' }}>
              <ComingSoonOverlay onClose={() => setActiveSection('dashboard')} className="higher-position" />
              <div className="live-learning-tabs">
                <button 
                  className={`live-tab ${activeLiveTab === 'book-lesson' ? 'active' : ''}`}
                  onClick={() => setActiveLiveTab('book-lesson')}
                >
                  <i className="material-icons">event</i>
                  Book a Lesson
                </button>
                <button 
                  className={`live-tab ${activeLiveTab === 'my-bookings' ? 'active' : ''}`}
                  onClick={() => setActiveLiveTab('my-bookings')}
                >
                  <i className="material-icons">schedule</i>
                  My Bookings
                  <span className="badge">3</span>
                </button>
                <button 
                  className={`live-tab ${activeLiveTab === 'my-teachers' ? 'active' : ''}`}
                  onClick={() => setActiveLiveTab('my-teachers')}
                >
                  <i className="material-icons">school</i>
                  My Teachers
                </button>
                <button 
                  className={`live-tab ${activeLiveTab === 'progress' ? 'active' : ''}`}
                  onClick={() => setActiveLiveTab('progress')}
                >
                  <i className="material-icons">trending_up</i>
                  Progress
                </button>
                <button 
                  className={`live-tab ${activeLiveTab === 'rate-lesson' ? 'active' : ''}`}
                  onClick={() => setActiveLiveTab('rate-lesson')}
                >
                  <i className="material-icons">rate_review</i>
                  Rate a Lesson
                </button>
              </div>

              {/* Live Learning Content */}
              <div className="live-learning-content">
                {/* Book a Lesson Section */}
                {activeLiveTab === 'book-lesson' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">event</i>
                        Book a Lesson
                      </div>
                      <p className="user-section-subtitle">Find the perfect teacher for your language learning journey</p>
                    </div>

                    {/* Search and Filter */}
                    <div className="live-search-filter">
                      <div className="search-box">
                        <i className="material-icons">search</i>
                        <input 
                          type="text" 
                          placeholder="Search teachers by name or specialty..."
                          value={teacherSearch}
                          onChange={(e) => setTeacherSearch(e.target.value)}
                        />
                      </div>
                      <div className="filter-options">
                        <select 
                          value={specialtyFilter} 
                          onChange={(e) => setSpecialtyFilter(e.target.value)}
                        >
                          <option value="">All Specialties</option>
                          <option value="english">English</option>
                          <option value="spanish">Spanish</option>
                          <option value="french">French</option>
                          <option value="german">German</option>
                          <option value="chinese">Chinese</option>
                          <option value="ielts">IELTS</option>
                          <option value="toefl">TOEFL</option>
                        </select>
                        <select 
                          value={ratingFilter} 
                          onChange={(e) => setRatingFilter(e.target.value)}
                        >
                          <option value="">All Ratings</option>
                          <option value="5">5 Stars</option>
                          <option value="4">4+ Stars</option>
                          <option value="3">3+ Stars</option>
                        </select>
                        <select 
                          value={availabilityFilter} 
                          onChange={(e) => setAvailabilityFilter(e.target.value)}
                        >
                          <option value="">All Availability</option>
                          <option value="today">Available Today</option>
                          <option value="tomorrow">Available Tomorrow</option>
                          <option value="week">This Week</option>
                        </select>
                      </div>
                    </div>

                    {/* Teachers Grid */}
                    <div className="teachers-grid">
                      {filteredTeachers.length === 0 ? (
                        <div className="no-results">
                          <i className="material-icons">search_off</i>
                          <h3>No teachers found</h3>
                          <p>Try adjusting your search criteria</p>
                        </div>
                      ) : (
                        filteredTeachers.map(teacher => (
                          <div key={teacher.id} className="teacher-card">
                            <div className="teacher-image">
                              <img src={teacher.image} alt={teacher.name} />
                              <div className={`teacher-status ${teacher.availability.includes('Today') ? 'online' : 'offline'}`}>
                                {teacher.availability.includes('Today') ? 'Online' : 'Offline'}
                              </div>
                            </div>
                            <div className="teacher-content">
                              <h3 className="teacher-title">{teacher.name}</h3>
                              <p className="teacher-specialty">{teacher.specialty}</p>
                              <div className="teacher-rating">
                                <span className="stars">{generateStars(teacher.rating)}</span>
                                <span className="rating-text">{teacher.rating}/5.0 ({teacher.totalStudents} reviews)</span>
                              </div>
                              <p className="teacher-description">{teacher.bio}</p>
                              <div className="teacher-meta">
                                <div className="teacher-price-info">
                                  <span className="teacher-price">${teacher.hourlyRate}/hour</span>
                                  <span className="teacher-availability">{teacher.availability}</span>
                                </div>
                                <span className={`teacher-status-badge ${teacher.availability.includes('Today') ? 'online' : 'offline'}`}>
                                  {teacher.availability.includes('Today') ? 'Online Now' : 'Offline'}
                                </span>
                              </div>
                              <div className="teacher-actions">
                                <button className="user-course-btn primary" onClick={() => openBookingModal(teacher.id)}>
                                  <i className="material-icons">event</i>
                                  Book Lesson
                                </button>
                                <button className="user-course-btn" onClick={() => viewTeacherProfile(teacher.id)}>
                                  <i className="material-icons">info</i>
                                  View Profile
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* My Bookings Section */}
                {activeLiveTab === 'my-bookings' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">schedule</i>
                        My Bookings
                      </div>
                      <p>Manage your upcoming and past lessons</p>
                    </div>

                    {/* Booking Status Tabs */}
                    <div className="booking-tabs">
                      <button 
                        className={`booking-tab ${activeBookingTab === 'upcoming' ? 'active' : ''}`}
                        onClick={() => setActiveBookingTab('upcoming')}
                      >
                        Upcoming
                        <span className="tab-count">{bookings.upcoming.length}</span>
                      </button>
                      <button 
                        className={`booking-tab ${activeBookingTab === 'completed' ? 'active' : ''}`}
                        onClick={() => setActiveBookingTab('completed')}
                      >
                        Completed
                        <span className="tab-count">{bookings.completed.length}</span>
                      </button>
                      <button 
                        className={`booking-tab ${activeBookingTab === 'cancelled' ? 'active' : ''}`}
                        onClick={() => setActiveBookingTab('cancelled')}
                      >
                        Cancelled
                        <span className="tab-count">{bookings.cancelled.length}</span>
                      </button>
                    </div>

                    {/* Upcoming Bookings */}
                    {activeBookingTab === 'upcoming' && (
                      <div className="bookings-container">
                        {bookings.upcoming.length === 0 ? (
                          <div className="no-bookings">
                            <i className="material-icons">schedule</i>
                            <h3>No upcoming bookings</h3>
                            <p>Book your first lesson to get started!</p>
                          </div>
                        ) : (
                          bookings.upcoming.map(booking => (
                            <div key={booking.id} className="booking-card">
                              <div className="booking-teacher">
                                <img src={booking.teacherImage} alt={booking.teacherName} />
                                <div className="booking-teacher-info">
                                  <h4>{booking.teacherName}</h4>
                                  <p>{booking.subject}</p>
                                </div>
                              </div>
                              <div className="booking-details">
                                <div className="booking-time">
                                  <i className="material-icons">schedule</i>
                                  <span>{formatDate(booking.date)} at {booking.time} ({booking.duration} min)</span>
                                </div>
                                <div className="booking-status scheduled">Scheduled</div>
                              </div>
                              <div className="booking-actions">
                                <button className="user-course-btn primary" onClick={() => joinLesson(booking.id)}>
                                  <i className="material-icons">play_arrow</i>
                                  Join Session
                                </button>
                                <button className="user-course-btn" onClick={() => rescheduleBooking(booking.id)}>
                                  <i className="material-icons">reschedule</i>
                                  Reschedule
                                </button>
                                <button className="user-course-btn danger" onClick={() => cancelBooking(booking.id)}>
                                  <i className="material-icons">cancel</i>
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}

                    {/* Completed Bookings */}
                    {activeBookingTab === 'completed' && (
                      <div className="bookings-container">
                        {bookings.completed.length === 0 ? (
                          <div className="no-bookings">
                            <i className="material-icons">check_circle</i>
                            <h3>No completed lessons</h3>
                            <p>Complete your first lesson to see it here!</p>
                          </div>
                        ) : (
                          bookings.completed.map(booking => (
                            <div key={booking.id} className="booking-card completed">
                              <div className="booking-teacher">
                                <img src={booking.teacherImage} alt={booking.teacherName} />
                                <div className="booking-teacher-info">
                                  <h4>{booking.teacherName}</h4>
                                  <p>{booking.subject}</p>
                                </div>
                              </div>
                              <div className="booking-details">
                                <div className="booking-time">
                                  <i className="material-icons">schedule</i>
                                  <span>{formatDate(booking.date)} at {booking.time} ({booking.duration} min)</span>
                                </div>
                                <div className="booking-status completed">Completed</div>
                              </div>
                              <div className="booking-actions">
                                <button className="user-course-btn primary" onClick={() => rateLesson(booking.id)}>
                                  <i className="material-icons">rate_review</i>
                                  Rate Lesson
                                </button>
                                <button className="user-course-btn">
                                  <i className="material-icons">event</i>
                                  Book Again
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}

                    {/* Cancelled Bookings */}
                    {activeBookingTab === 'cancelled' && (
                      <div className="bookings-container">
                        {bookings.cancelled.length === 0 ? (
                          <div className="no-bookings">
                            <i className="material-icons">cancel</i>
                            <h3>No cancelled bookings</h3>
                            <p>Great! You haven't cancelled any lessons.</p>
                          </div>
                        ) : (
                          bookings.cancelled.map(booking => (
                            <div key={booking.id} className="booking-card cancelled">
                              <div className="booking-teacher">
                                <img src={booking.teacherImage} alt={booking.teacherName} />
                                <div className="booking-teacher-info">
                                  <h4>{booking.teacherName}</h4>
                                  <p>{booking.subject}</p>
                                </div>
                              </div>
                              <div className="booking-details">
                                <div className="booking-time">
                                  <i className="material-icons">schedule</i>
                                  <span>{formatDate(booking.date)} at {booking.time} ({booking.duration} min)</span>
                                </div>
                                <div className="booking-status cancelled">Cancelled</div>
                              </div>
                              <div className="booking-actions">
                                <button className="user-course-btn">
                                  <i className="material-icons">event</i>
                                  Book Again
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* My Teachers Section */}
                {activeLiveTab === 'my-teachers' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">school</i>
                        My Teachers
                      </div>
                      <p className="user-section-subtitle">Your favorite teachers and learning history</p>
                    </div>

                    <div className="teachers-grid">
                      {myTeachers.length === 0 ? (
                        <div className="no-teachers">
                          <i className="material-icons">school</i>
                          <h3>No teachers yet</h3>
                          <p>Book your first lesson to start building your teacher network!</p>
                        </div>
                      ) : (
                        myTeachers.map(teacher => (
                          <div key={teacher.id} className="teacher-card">
                            <div className="teacher-image">
                              <img src={teacher.image} alt={teacher.name} />
                              <div className="teacher-status online">Online</div>
                            </div>
                            <div className="teacher-content">
                              <h3 className="teacher-title">{teacher.name}</h3>
                              <p className="teacher-specialty">{teacher.specialty} • {teacher.lessonsCompleted} lessons completed</p>
                              <div className="teacher-rating">
                                <span className="stars">{generateStars(teacher.rating)}</span>
                                <span className="rating-text">{teacher.rating} rating</span>
                              </div>
                              <p className="teacher-description">
                                Your trusted language partner. Great for consistent learning and progress tracking.
                              </p>
                              <div className="teacher-meta">
                                <div className="teacher-price-info">
                                  <span className="teacher-price">$25/hour</span>
                                  <span className="teacher-availability">Last lesson: {formatDate(teacher.lastLesson)}</span>
                                </div>
                                <span className="teacher-status-badge online">Online Now</span>
                              </div>
                              <div className="teacher-actions">
                                <button className="user-course-btn primary" onClick={() => openBookingModal(teacher.id)}>
                                  <i className="material-icons">event</i>
                                  Book Again
                                </button>
                                <button className="user-course-btn" onClick={() => viewTeacherProfile(teacher.id)}>
                                  <i className="material-icons">message</i>
                                  Message
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Progress Section */}
                {activeLiveTab === 'progress' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">trending_up</i>
                        Learning Progress
                      </div>
                      <p className="user-section-subtitle">Track your language learning journey and achievements</p>
                    </div>

                    {/* Progress Overview Cards */}
                    <div className="progress-stats">
                      <div className="progress-card">
                        <div className="progress-icon">
                          <i className="material-icons">schedule</i>
                        </div>
                        <div className="progress-info">
                          <h3>Total Lessons</h3>
                          <div className="progress-number">47</div>
                          <div className="progress-period">This year</div>
                        </div>
                      </div>

                      <div className="progress-card">
                        <div className="progress-icon">
                          <i className="material-icons">timer</i>
                        </div>
                        <div className="progress-info">
                          <h3>Study Hours</h3>
                          <div className="progress-number">89</div>
                          <div className="progress-period">Total hours</div>
                        </div>
                      </div>

                      <div className="progress-card">
                        <div className="progress-icon">
                          <i className="material-icons">star</i>
                        </div>
                        <div className="progress-info">
                          <h3>Average Rating</h3>
                          <div className="progress-number">4.8</div>
                          <div className="progress-period">From teachers</div>
                        </div>
                      </div>

                      <div className="progress-card">
                        <div className="progress-icon">
                          <i className="material-icons">language</i>
                        </div>
                        <div className="progress-info">
                          <h3>Languages</h3>
                          <div className="progress-number">3</div>
                          <div className="progress-period">Spanish, English, French</div>
                        </div>
                      </div>
                    </div>

                    {/* Learning Streak */}
                    <div className="streak-container">
                      <h3>Current Learning Streak</h3>
                      <div className="streak-info">
                        <div className="streak-number">12</div>
                        <div className="streak-text">
                          <p>Days</p>
                          <span>Keep it up! You're doing great.</span>
                        </div>
                      </div>
                    </div>

                    {/* Language Progress */}
                    <div className="user-section">
                      <div className="user-section-header">
                        <div className="user-section-title">
                          <i className="material-icons">language</i>
                          Language Progress
                        </div>
                      </div>
                      
                      <div className="language-progress-grid">
                        <div className="language-progress-card">
                          <div className="language-header">
                            <h4>Spanish</h4>
                            <span className="level-badge intermediate">Intermediate</span>
                          </div>
                          <div className="progress-bar">
                            <div className="progress-fill" style={{width: '75%'}}></div>
                          </div>
                          <p>15 lessons completed • 75% to Advanced</p>
                        </div>

                        <div className="language-progress-card">
                          <div className="language-header">
                            <h4>English</h4>
                            <span className="level-badge advanced">Advanced</span>
                          </div>
                          <div className="progress-bar">
                            <div className="progress-fill" style={{width: '90%'}}></div>
                          </div>
                          <p>8 lessons completed • 90% to Fluent</p>
                        </div>

                        <div className="language-progress-card">
                          <div className="language-header">
                            <h4>French</h4>
                            <span className="level-badge beginner">Beginner</span>
                          </div>
                          <div className="progress-bar">
                            <div className="progress-fill" style={{width: '30%'}}></div>
                          </div>
                          <p>3 lessons completed • 30% to Intermediate</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Rate a Lesson Section */}
                {activeLiveTab === 'rate-lesson' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">rate_review</i>
                        Rate a Lesson
                      </div>
                      <p className="user-section-subtitle">Share your feedback with teachers to help them improve</p>
                    </div>

                    <div className="rating-container">
                      {ratings.length === 0 ? (
                        <div className="no-ratings">
                          <i className="material-icons">rate_review</i>
                          <h3>No lessons to rate</h3>
                          <p>Complete a lesson to rate your teacher!</p>
                        </div>
                      ) : (
                        ratings.map(rating => (
                          <div key={rating.id} className="rating-card">
                            <div className="rating-teacher">
                              <img src="/placeholder-avatar.jpg" alt={rating.teacherName} />
                              <div className="rating-teacher-info">
                                <h4>{rating.teacherName}</h4>
                                <p>{rating.subject} • {formatDate(rating.date)}</p>
                              </div>
                            </div>
                            {rating.canRate ? (
                              <button className="user-course-btn primary" onClick={() => openRatingModal(rating.id)}>
                                <i className="material-icons">rate_review</i>
                                Rate Lesson
                              </button>
                            ) : (
                              <span className="rated-badge">Already Rated</span>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case 'shop':
        return (
          <div className="teacher-main-content">
            <div className="teacher-content-header">
              <h1 className="teacher-content-title">Educational Shop</h1>
              <p className="teacher-content-subtitle">Discover premium courses, books, and learning materials</p>
            </div>
            <div style={{ position: 'relative', minHeight: '100vh' }}>
              <ComingSoonOverlay onClose={() => setActiveSection('dashboard')} className="higher-position" />
            {/* Shop Controls */}
            <div className="teacher-section">
              <div className="shop-controls">
                <div className="shop-search">
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <button onClick={handleSearch}>
                    <i className="material-icons">search</i>
                  </button>
                </div>
                
                <div className="shop-filters">
                  <select 
                    value={priceFilter} 
                    onChange={(e) => {
                      setPriceFilter(e.target.value);
                      handleFilterChange();
                    }}
                  >
                    <option value="">All Prices</option>
                    <option value="0-50">Under $50</option>
                    <option value="50-100">$50 - $100</option>
                    <option value="100-200">$100 - $200</option>
                    <option value="200+">$200+</option>
                  </select>
                  
                  <select 
                    value={sortBy} 
                    onChange={(e) => {
                      setSortBy(e.target.value);
                      handleSortChange();
                    }}
                  >
                    <option value="name">Sort by Name</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="rating">Highest Rated</option>
                    <option value="newest">Newest First</option>
                  </select>
                </div>

                <div className="shop-cart-info">
                  <button className="cart-btn" onClick={() => setActiveShopTab('cart')}>
                    <i className="material-icons">shopping_cart</i>
                    <span className="cart-count">{getCartCount()}</span>
                  </button>
                  <button className="wishlist-btn" onClick={() => setActiveShopTab('wishlist')}>
                    <i className="material-icons">favorite</i>
                    <span className="wishlist-count">{wishlist.length}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Shop Tabs */}
            <div className="teacher-section">
              <div className="shop-tabs">
                <button 
                  className={`shop-tab ${activeShopTab === 'courses' ? 'active' : ''}`}
                  onClick={() => setActiveShopTab('courses')}
                >
                  <i className="material-icons">school</i>
                  Online Courses
                </button>
                <button 
                  className={`shop-tab ${activeShopTab === 'books' ? 'active' : ''}`}
                  onClick={() => setActiveShopTab('books')}
                >
                  <i className="material-icons">book</i>
                  Books & Materials
                </button>
                <button 
                  className={`shop-tab ${activeShopTab === 'tools' ? 'active' : ''}`}
                  onClick={() => setActiveShopTab('tools')}
                >
                  <i className="material-icons">build</i>
                  Learning Tools
                </button>
                <button 
                  className={`shop-tab ${activeShopTab === 'software' ? 'active' : ''}`}
                  onClick={() => setActiveShopTab('software')}
                >
                  <i className="material-icons">computer</i>
                  Software & Apps
                </button>
                <button 
                  className={`shop-tab ${activeShopTab === 'memberships' ? 'active' : ''}`}
                  onClick={() => setActiveShopTab('memberships')}
                >
                  <i className="material-icons">card_membership</i>
                  Memberships
                </button>
                <button 
                  className={`shop-tab ${activeShopTab === 'certificates' ? 'active' : ''}`}
                  onClick={() => setActiveShopTab('certificates')}
                >
                  <i className="material-icons">verified</i>
                  Certificates
                </button>
                <button 
                  className={`shop-tab ${activeShopTab === 'tutoring' ? 'active' : ''}`}
                  onClick={() => setActiveShopTab('tutoring')}
                >
                  <i className="material-icons">person_add</i>
                  Tutoring Services
                </button>
                <button 
                  className={`shop-tab ${activeShopTab === 'resources' ? 'active' : ''}`}
                  onClick={() => setActiveShopTab('resources')}
                >
                  <i className="material-icons">folder</i>
                  Study Resources
                </button>
              </div>

              {/* Shop Content */}
              <div className="shop-content">
                {activeShopTab === 'courses' && (
                  <div className="teacher-section">
                    <div className="teacher-section-header">
                      <div className="teacher-section-title">
                        <i className="material-icons">school</i>
                        Online Courses
                      </div>
                      <p className="teacher-section-subtitle">Browse and purchase premium online courses</p>
                    </div>
                    
                    <div className="teacher-courses-grid">
                      {loading ? (
                        <div className="loading">Loading courses...</div>
                      ) : (
                        <>
                          {/* Sample Course Cards */}
                          <div className="teacher-course-card">
                            <div className="teacher-course-image">
                              <div className="placeholder">
                                <i className="material-icons">school</i>
                              </div>
                            </div>
                            <div className="teacher-course-content">
                              <h3>Advanced English Grammar</h3>
                              <p>Master complex grammar structures and improve your writing skills</p>
                              <div className="teacher-course-meta">
                                <span className="price">$49.99</span>
                                <span className="rating">
                                  {generateStars(4.8)}
                                  <span className="rating-text">4.8</span>
                                </span>
                              </div>
                              <div className="teacher-course-actions">
                                <button className="teacher-course-btn primary" onClick={() => addToCart('course1')}>
                                  <i className="material-icons">add_shopping_cart</i>
                                  Add to Cart
                                </button>
                                <button className="teacher-course-btn" onClick={() => toggleWishlistItem('course1')}>
                                  <i className="material-icons">favorite_border</i>
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="teacher-course-card">
                            <div className="teacher-course-image">
                              <div className="placeholder">
                                <i className="material-icons">school</i>
                              </div>
                            </div>
                            <div className="teacher-course-content">
                              <h3>Business English Speaking</h3>
                              <p>Develop professional communication skills for the workplace</p>
                              <div className="teacher-course-meta">
                                <span className="price">$39.99</span>
                                <span className="rating">
                                  {generateStars(4.9)}
                                  <span className="rating-text">4.9</span>
                                </span>
                              </div>
                              <div className="teacher-course-actions">
                                <button className="teacher-course-btn primary" onClick={() => addToCart('course2')}>
                                  <i className="material-icons">add_shopping_cart</i>
                                  Add to Cart
                                </button>
                                <button className="teacher-course-btn" onClick={() => toggleWishlistItem('course2')}>
                                  <i className="material-icons">favorite_border</i>
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="teacher-course-card">
                            <div className="teacher-course-image">
                              <div className="placeholder">
                                <i className="material-icons">school</i>
                              </div>
                            </div>
                            <div className="teacher-course-content">
                              <h3>IELTS Preparation Course</h3>
                              <p>Comprehensive preparation for IELTS exam success</p>
                              <div className="teacher-course-meta">
                                <span className="price">$79.99</span>
                                <span className="rating">
                                  {generateStars(4.7)}
                                  <span className="rating-text">4.7</span>
                                </span>
                              </div>
                              <div className="teacher-course-actions">
                                <button className="teacher-course-btn primary" onClick={() => addToCart('course3')}>
                                  <i className="material-icons">add_shopping_cart</i>
                                  Add to Cart
                                </button>
                                <button className="teacher-course-btn" onClick={() => toggleWishlistItem('course3')}>
                                  <i className="material-icons">favorite_border</i>
                                </button>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {activeShopTab === 'cart' && (
                  <div className="teacher-section">
                    <div className="teacher-section-header">
                      <div className="teacher-section-title">
                        <i className="material-icons">shopping_cart</i>
                        Shopping Cart
                      </div>
                      <div className="cart-total">
                        Total: ${getCartTotal().toFixed(2)}
                      </div>
                    </div>
                    
                    {cart.length === 0 ? (
                      <div className="empty-cart">
                        <i className="material-icons">shopping_cart</i>
                        <h3>Your cart is empty</h3>
                        <p>Add some products to get started!</p>
                        <button className="teacher-course-btn primary" onClick={() => setActiveShopTab('courses')}>
                          <i className="material-icons">shopping_bag</i>
                          Browse Products
                        </button>
                      </div>
                    ) : (
                      <div className="cart-items">
                        {cart.map(item => (
                          <div key={item.id} className="cart-item">
                            <div className="cart-item-image">
                              <div className="placeholder">
                                <i className="material-icons">school</i>
                              </div>
                            </div>
                            <div className="cart-item-content">
                              <h3>{item.name}</h3>
                              <p>{item.description}</p>
                              <div className="cart-item-meta">
                                <span className="price">${item.price}</span>
                                <div className="quantity-controls">
                                  <button onClick={() => updateCartQuantity(item.id, item.quantity - 1)}>-</button>
                                  <span>{item.quantity}</span>
                                  <button onClick={() => updateCartQuantity(item.id, item.quantity + 1)}>+</button>
                                </div>
                              </div>
                            </div>
                            <div className="cart-item-actions">
                              <button className="teacher-course-btn danger" onClick={() => removeFromCart(item.id)}>
                                <i className="material-icons">delete</i>
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}
                        
                        <div className="cart-actions">
                          <button className="teacher-course-btn secondary" onClick={() => setActiveShopTab('courses')}>
                            <i className="material-icons">arrow_back</i>
                            Continue Shopping
                          </button>
                          <button className="teacher-course-btn primary">
                            <i className="material-icons">payment</i>
                            Checkout
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeShopTab === 'wishlist' && (
                  <div className="teacher-section">
                    <div className="teacher-section-header">
                      <div className="teacher-section-title">
                        <i className="material-icons">favorite</i>
                        Wishlist
                      </div>
                    </div>
                    
                    {wishlist.length === 0 ? (
                      <div className="empty-wishlist">
                        <i className="material-icons">favorite_border</i>
                        <h3>Your wishlist is empty</h3>
                        <p>Save products you like for later!</p>
                        <button className="teacher-course-btn primary" onClick={() => setActiveShopTab('courses')}>
                          <i className="material-icons">shopping_bag</i>
                          Browse Products
                        </button>
                      </div>
                    ) : (
                      <div className="wishlist-items">
                        {wishlist.map(item => (
                          <div key={item.id} className="wishlist-item">
                            <div className="wishlist-item-image">
                              <div className="placeholder">
                                <i className="material-icons">school</i>
                              </div>
                            </div>
                            <div className="wishlist-item-content">
                              <h3>{item.name}</h3>
                              <p>{item.description}</p>
                              <div className="wishlist-item-meta">
                                <span className="price">${item.price}</span>
                                <span className="rating">
                                  {generateStars(item.rating)}
                                  <span className="rating-text">{item.rating}</span>
                                </span>
                              </div>
                            </div>
                            <div className="wishlist-item-actions">
                              <button className="teacher-course-btn primary" onClick={() => addToCart(item.id)}>
                                <i className="material-icons">add_shopping_cart</i>
                                Add to Cart
                              </button>
                              <button className="teacher-course-btn" onClick={() => toggleWishlistItem(item.id)}>
                                <i className="material-icons">favorite</i>
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Other shop tabs can be implemented similarly */}
                {activeShopTab === 'books' && (
                  <div className="teacher-section">
                    <div className="teacher-section-header">
                      <div className="teacher-section-title">
                        <i className="material-icons">book</i>
                        Books & Materials
                      </div>
                      <p className="teacher-section-subtitle">Educational books and study materials</p>
                    </div>
                    <p>Books and materials section will be implemented here...</p>
                  </div>
                )}

                {activeShopTab === 'tools' && (
                  <div className="teacher-section">
                    <div className="teacher-section-header">
                      <div className="teacher-section-title">
                        <i className="material-icons">build</i>
                        Learning Tools
                      </div>
                      <p className="teacher-section-subtitle">Tools and resources for effective learning</p>
                    </div>
                    <p>Learning tools section will be implemented here...</p>
                  </div>
                )}

                {activeShopTab === 'software' && (
                  <div className="teacher-section">
                    <div className="teacher-section-header">
                      <div className="teacher-section-title">
                        <i className="material-icons">computer</i>
                        Software & Apps
                      </div>
                      <p className="teacher-section-subtitle">Educational software and applications</p>
                    </div>
                    <p>Software and apps section will be implemented here...</p>
                  </div>
                )}

                {activeShopTab === 'memberships' && (
                  <div className="teacher-section">
                    <div className="teacher-section-header">
                      <div className="teacher-section-title">
                        <i className="material-icons">card_membership</i>
                        Memberships
                      </div>
                      <p className="teacher-section-subtitle">Premium membership plans</p>
                    </div>
                    <p>Memberships section will be implemented here...</p>
                  </div>
                )}

                {activeShopTab === 'certificates' && (
                  <div className="teacher-section">
                    <div className="teacher-section-header">
                      <div className="teacher-section-title">
                        <i className="material-icons">verified</i>
                        Certificates
                      </div>
                      <p className="teacher-section-subtitle">Professional certification programs</p>
                    </div>
                    <p>Certificates section will be implemented here...</p>
                  </div>
                )}

                {activeShopTab === 'tutoring' && (
                  <div className="teacher-section">
                    <div className="teacher-section-header">
                      <div className="teacher-section-title">
                        <i className="material-icons">person_add</i>
                        Tutoring Services
                      </div>
                      <p className="teacher-section-subtitle">One-on-one tutoring sessions</p>
                    </div>
                    <p>Tutoring services section will be implemented here...</p>
                  </div>
                )}

                {activeShopTab === 'resources' && (
                  <div className="teacher-section">
                    <div className="teacher-section-header">
                      <div className="teacher-section-title">
                        <i className="material-icons">folder</i>
                        Study Resources
                      </div>
                      <p className="teacher-section-subtitle">Additional study materials and resources</p>
                    </div>
                    <p>Study resources section will be implemented here...</p>
                  </div>
                )}
                             </div>
                             </div>
             </div>
           </div>
         );
      case 'course-library':
        return (
          <div className="teacher-learning-content" key={`course-library-${sectionKey}`}>
            <UserDashboard
              initialSection="course-library"
              hideSidebar={true}
            />
          </div>
        );
       case 'free-talk':
        return (
          <div className="teacher-main-content">
            <div className="teacher-content-header">
              <h1 className="teacher-content-title">Free Talk</h1>
              <p className="teacher-content-subtitle">Practice & Connect with learners worldwide</p>
            </div>

            {/* Talk Navigation Tabs */}
            <div className="teacher-section" style={{ position: 'relative' }}>
              <ComingSoonOverlay onClose={() => setActiveSection('dashboard')} className="higher-position" />
              <div className="talk-tabs">
                <button 
                  className={`talk-tab ${activeTalkTab === 'communication-hub' ? 'active' : ''}`}
                  onClick={() => setActiveTalkTab('communication-hub')}
                >
                  <i className="material-icons">home</i>
                  Communication Hub
                </button>
                <button 
                  className={`talk-tab ${activeTalkTab === 'create-room' ? 'active' : ''}`}
                  onClick={() => setActiveTalkTab('create-room')}
                >
                  <i className="material-icons">add_circle</i>
                  Create Room
                </button>
                <button 
                  className={`talk-tab ${activeTalkTab === 'my-activity' ? 'active' : ''}`}
                  onClick={() => setActiveTalkTab('my-activity')}
                >
                  <i className="material-icons">history</i>
                  My Activity
                </button>
                <button 
                  className={`talk-tab ${activeTalkTab === 'profile-settings' ? 'active' : ''}`}
                  onClick={() => setActiveTalkTab('profile-settings')}
                >
                  <i className="material-icons">settings</i>
                  Profile Settings
                </button>
              </div>

              {/* Talk Content */}
              <div className="talk-content">
                {/* Communication Hub Section */}
                {activeTalkTab === 'communication-hub' && (
                  <div className="teacher-section">
                    <div className="teacher-section-header">
                      <div className="teacher-section-title">
                        <i className="material-icons">home</i>
                        Communication Hub
                      </div>
                      <p className="teacher-section-subtitle">Join active rooms and practice with learners worldwide</p>
                    </div>

                    {/* Search and Filter */}
                    <div className="search-filter-container">
                      <div className="search-box">
                        <i className="material-icons">search</i>
                        <input 
                          type="text" 
                          placeholder="Search rooms by name or topic..." 
                          value={roomSearch}
                          onChange={(e) => setRoomSearch(e.target.value)}
                        />
                      </div>
                      <div className="filter-options">
                        <select 
                          value={languageFilter}
                          onChange={(e) => setLanguageFilter(e.target.value)}
                        >
                          <option value="">All Languages</option>
                          <option value="english">English</option>
                          <option value="french">French</option>
                          <option value="arabic">Arabic</option>
                          <option value="spanish">Spanish</option>
                          <option value="german">German</option>
                          <option value="chinese">Chinese</option>
                        </select>
                        <select 
                          value={typeFilter}
                          onChange={(e) => setTypeFilter(e.target.value)}
                        >
                          <option value="">All Types</option>
                          <option value="voice">Voice Only</option>
                          <option value="text">Text Only</option>
                          <option value="both">Voice & Text</option>
                        </select>
                        <select 
                          value={levelFilterTalk}
                          onChange={(e) => setLevelFilterTalk(e.target.value)}
                        >
                          <option value="">All Levels</option>
                          <option value="beginner">Beginner</option>
                          <option value="intermediate">Intermediate</option>
                          <option value="advanced">Advanced</option>
                        </select>
                        <select 
                          value={categoryFilterTalk}
                          onChange={(e) => setCategoryFilterTalk(e.target.value)}
                        >
                          <option value="">All Categories</option>
                          <option value="conversation">Conversation</option>
                          <option value="ielts">IELTS</option>
                          <option value="grammar">Grammar</option>
                          <option value="business">Business</option>
                          <option value="fun">Fun Talk</option>
                        </select>
                      </div>
                    </div>

                    {/* Active Rooms Grid */}
                    <div className="rooms-grid">
                      {filteredRooms.length === 0 ? (
                        <div className="no-results">
                          <i className="material-icons">search_off</i>
                          <h3>No rooms found</h3>
                          <p>Try adjusting your search criteria</p>
                        </div>
                      ) : (
                        filteredRooms.map(room => (
                          <div key={room.id} className="room-card">
                            <div className="room-header">
                              <div>
                                <h3 className="room-title">{room.title}</h3>
                                <div className="room-meta">
                                  <span>{room.type === 'voice' ? '🎧' : room.type === 'text' ? '💬' : '🗣️'} {room.type.charAt(0).toUpperCase() + room.type.slice(1)}</span>
                                  <span>🌍 {room.language}</span>
                                  <span>📚 {room.level.charAt(0).toUpperCase() + room.level.slice(1)}</span>
                                </div>
                              </div>
                              {room.isPrivate && <span className="badge">🔒 Private</span>}
                            </div>
                            
                            <div className="room-tags">
                              {room.tags.map(tag => (
                                <span key={tag} className="room-tag">{tag}</span>
                              ))}
                            </div>
                            
                            <div className="room-creator">
                              <div className="creator-avatar">{room.creator.avatar}</div>
                              <span>Created by: {room.creator.name} {room.creator.country}</span>
                            </div>
                            
                            <div className="room-stats">
                              <div className="room-users">
                                <span>👥 {room.users}/{room.maxUsers} online</span>
                              </div>
                            </div>
                            
                            <div className="room-actions">
                              <button className="teacher-course-btn primary" onClick={() => joinRoom(room.id)}>
                                Join Room
                              </button>
                              <button className="teacher-course-btn" onClick={() => reportRoom(room.id)}>
                                Report
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Create Room Section */}
                {activeTalkTab === 'create-room' && (
                  <div className="teacher-section">
                    <div className="teacher-section-header">
                      <div className="teacher-section-title">
                        <i className="material-icons">add_circle</i>
                        Create a Room
                      </div>
                      <p className="teacher-section-subtitle">Start your own practice space and invite others to join</p>
                    </div>

                    <div className="create-room-form">
                      <form onSubmit={handleCreateRoomSubmit}>
                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="roomTitle" className="required">Room Title *</label>
                            <input 
                              type="text" 
                              id="roomTitle"
                              value={createRoomForm.title}
                              onChange={(e) => setCreateRoomForm(prev => ({ ...prev, title: e.target.value }))}
                              placeholder="e.g., Advanced English Conversation Practice" 
                              required 
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="roomLanguage" className="required">Language *</label>
                            <select 
                              id="roomLanguage"
                              value={createRoomForm.language}
                              onChange={(e) => setCreateRoomForm(prev => ({ ...prev, language: e.target.value }))}
                              required
                            >
                              <option value="">Select Language</option>
                              <option value="english">English</option>
                              <option value="french">French</option>
                              <option value="arabic">Arabic</option>
                              <option value="spanish">Spanish</option>
                              <option value="german">German</option>
                              <option value="chinese">Chinese</option>
                            </select>
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="roomType" className="required">Room Type *</label>
                            <select 
                              id="roomType"
                              value={createRoomForm.type}
                              onChange={(e) => setCreateRoomForm(prev => ({ ...prev, type: e.target.value }))}
                              required
                            >
                              <option value="">Select Type</option>
                              <option value="voice">Voice Only</option>
                              <option value="text">Text Only</option>
                              <option value="both">Voice & Text</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label htmlFor="roomCategory" className="required">Category *</label>
                            <select 
                              id="roomCategory"
                              value={createRoomForm.category}
                              onChange={(e) => setCreateRoomForm(prev => ({ ...prev, category: e.target.value }))}
                              required
                            >
                              <option value="">Select Category</option>
                              <option value="conversation">Conversation</option>
                              <option value="ielts">IELTS</option>
                              <option value="grammar">Grammar</option>
                              <option value="business">Business</option>
                              <option value="fun">Fun Talk</option>
                            </select>
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="roomLevel">Level</label>
                            <select 
                              id="roomLevel"
                              value={createRoomForm.level}
                              onChange={(e) => setCreateRoomForm(prev => ({ ...prev, level: e.target.value }))}
                            >
                              <option value="any">Any Level</option>
                              <option value="beginner">Beginner</option>
                              <option value="intermediate">Intermediate</option>
                              <option value="advanced">Advanced</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label htmlFor="maxUsers">Max Users</label>
                            <input 
                              type="number" 
                              id="maxUsers"
                              value={createRoomForm.maxUsers}
                              onChange={(e) => setCreateRoomForm(prev => ({ ...prev, maxUsers: parseInt(e.target.value) }))}
                              min="2" 
                              max="20" 
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label htmlFor="roomTags">Tags</label>
                          <input 
                            type="text" 
                            id="roomTags"
                            value={createRoomForm.tags}
                            onChange={(e) => setCreateRoomForm(prev => ({ ...prev, tags: e.target.value }))}
                            placeholder="e.g., #Advanced #Conversation #Practice (separate with spaces)" 
                          />
                          <div className="form-help">Enter tags starting with # and separate with spaces</div>
                        </div>

                        <div className="form-group">
                          <label htmlFor="roomDescription">Description</label>
                          <textarea 
                            id="roomDescription"
                            value={createRoomForm.description}
                            onChange={(e) => setCreateRoomForm(prev => ({ ...prev, description: e.target.value }))}
                            rows="3" 
                            placeholder="Describe what this room is about..."
                          ></textarea>
                        </div>

                        <div className="form-group">
                          <label className="checkbox-label">
                            <input 
                              type="checkbox" 
                              id="isPrivateRoom"
                              checked={createRoomForm.isPrivate}
                              onChange={(e) => setCreateRoomForm(prev => ({ ...prev, isPrivate: e.target.checked }))}
                            />
                            Make this room private (requires invite code)
                          </label>
                        </div>

                        <div className="form-actions">
                          <button type="submit" className="form-btn primary">
                            <i className="material-icons">add_circle</i>
                            Create Room
                          </button>
                          <button type="button" className="form-btn secondary" onClick={() => setActiveTalkTab('communication-hub')}>
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                {/* My Activity Section */}
                {activeTalkTab === 'my-activity' && (
                  <div className="teacher-section">
                    <div className="teacher-section-header">
                      <div className="teacher-section-title">
                        <i className="material-icons">history</i>
                        My Activity
                      </div>
                      <p className="teacher-section-subtitle">Track your participation and manage your rooms</p>
                    </div>

                    {/* Activity Tabs */}
                    <div className="activity-tabs">
                      <button className="activity-tab active">
                        <i className="material-icons">room</i>
                        My Rooms
                        <span className="tab-count">{myRooms.length}</span>
                      </button>
                      <button className="activity-tab">
                        <i className="material-icons">schedule</i>
                        Recently Joined
                        <span className="tab-count">{recentRooms.length}</span>
                      </button>
                      <button className="activity-tab">
                        <i className="material-icons">forum</i>
                        My Posts
                        <span className="tab-count">{myPosts.length}</span>
                      </button>
                      <button className="activity-tab">
                        <i className="material-icons">report</i>
                        Reported Users
                        <span className="tab-count">{reportedUsers.length}</span>
                      </button>
                    </div>

                    {/* My Rooms */}
                    <div className="my-rooms-list">
                      {myRooms.map(room => (
                        <div key={room.id} className="my-room-item">
                          <div className="room-info">
                            <h3>{room.title}</h3>
                            <div className="room-meta">
                              <span>🌍 {room.language}</span>
                              <span>{room.type === 'voice' ? '🎧' : room.type === 'text' ? '💬' : '🗣️'} {room.type}</span>
                              <span>📚 {room.level}</span>
                              <span>👥 {room.users}/{room.maxUsers}</span>
                            </div>
                          </div>
                          <div className="room-status">
                            <span className={`status-badge ${room.isActive ? 'active' : 'inactive'}`}>
                              {room.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <div className="room-actions">
                            <button className="teacher-course-btn primary">
                              <i className="material-icons">play_arrow</i>
                              Join
                            </button>
                            <button className="teacher-course-btn" onClick={() => editRoom(room.id)}>
                              <i className="material-icons">edit</i>
                              Edit
                            </button>
                            <button className="teacher-course-btn danger" onClick={() => deleteRoom(room.id)}>
                              <i className="material-icons">delete</i>
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Profile Settings Section */}
                {activeTalkTab === 'profile-settings' && (
                  <div className="teacher-section">
                    <div className="teacher-section-header">
                      <div className="teacher-section-title">
                        <i className="material-icons">settings</i>
                        Profile Settings
                      </div>
                      <p className="teacher-section-subtitle">Customize your profile and preferences</p>
                    </div>

                    <div className="profile-settings-form">
                      <form onSubmit={handleProfileSettingsSubmit}>
                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="username">Username *</label>
                            <input type="text" id="username" defaultValue="MsSmith" required />
                          </div>
                          <div className="form-group">
                            <label htmlFor="country">Country</label>
                            <select id="country">
                              <option value="">Select Country</option>
                              <option value="usa" selected>🇺🇸 United States</option>
                              <option value="uk">🇬🇧 United Kingdom</option>
                              <option value="france">🇫🇷 France</option>
                              <option value="spain">🇪🇸 Spain</option>
                              <option value="germany">🇩🇪 Germany</option>
                              <option value="china">🇨🇳 China</option>
                            </select>
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="nativeLanguage">Native Language</label>
                            <select id="nativeLanguage">
                              <option value="">Select Language</option>
                              <option value="english" selected>English</option>
                              <option value="french">French</option>
                              <option value="arabic">Arabic</option>
                              <option value="spanish">Spanish</option>
                              <option value="german">German</option>
                              <option value="chinese">Chinese</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label htmlFor="learningLanguage">Learning Language</label>
                            <select id="learningLanguage">
                              <option value="">Select Language</option>
                              <option value="spanish" selected>Spanish</option>
                              <option value="french">French</option>
                              <option value="arabic">Arabic</option>
                              <option value="english">English</option>
                              <option value="german">German</option>
                              <option value="chinese">Chinese</option>
                            </select>
                          </div>
                        </div>

                        <div className="form-group">
                          <label htmlFor="languageLevel">Language Level</label>
                          <select id="languageLevel">
                            <option value="">Select Level</option>
                            <option value="beginner">Beginner</option>
                            <option value="intermediate">Intermediate</option>
                            <option value="advanced" selected>Advanced</option>
                          </select>
                        </div>

                        <button type="submit" className="form-btn primary">Save Settings</button>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case 'explore-courses':
        return (
          <div className="teacher-learning-content" key={`explore-courses-${sectionKey}`}>
            <UserDashboard
              initialSection="explore-courses"
              hideSidebar={true}
              unlockExploreCourses={true}
            />
          </div>
        );
      case 'my-learning-courses':
        return (
          <div className="teacher-learning-content" key={`my-learning-courses-${sectionKey}`}>
            <UserDashboard
              initialSection="courses"
              hideSidebar={true}
              unlockExploreCourses={true}
            />
          </div>
        );
      default:
        return <TeacherSectionDashboard />;
    }
  };

  const handleTopBarSearch = (query) => {
    // Handle global search from TopBar for teacher dashboard
    console.log('Teacher dashboard global search:', query);
    // You can implement search functionality here
    // For example: filter courses, search students, etc.
  };

  const handleTopBarNotificationClick = () => {
    setActiveSection('notifications');
    closeMobileSidebar();
  };

  const handleTopBarTabChange = (section) => {
    if (activeSection === section) {
      setSectionKey(prev => prev + 1);
    }
    if (section === 'my-students') {
      navigate('/teacher-dashboard/my-students');
    } else if (location.pathname.startsWith('/teacher-dashboard/my-students')) {
      navigate('/teacher-dashboard');
    }
    setActiveSection(section);
    closeMobileSidebar();
  };

  const handleSidebarSectionChange = (section) => {
    if (activeSection === section) {
      setSectionKey(prev => prev + 1);
    }
    if (section === 'my-students') {
      navigate('/teacher-dashboard/my-students');
    } else if (location.pathname.startsWith('/teacher-dashboard/my-students')) {
      navigate('/teacher-dashboard');
    }
    setActiveSection(section);
    closeMobileSidebar();
  };

  return (
    <div className="teacher-dashboard">
      {sidebarOpen && (
        <div
          className="teacher-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      <TeacherSidebar
        activeSection={activeSection}
        setActiveSection={handleSidebarSectionChange}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <TopBar
        onSearch={handleTopBarSearch}
        onNotificationClick={handleTopBarNotificationClick}
        onTabChange={handleTopBarTabChange}
        onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
        sidebarOpen={sidebarOpen}
      />
      <div className="teacher-main-content-wrapper">
        {renderSectionContent()}
      </div>
      
      {/* Notification */}
      {notification.show && (
        <div className={`teacher-notification ${notification.type}`}>
          <span className="notification-text">{notification.message}</span>
          <button 
            className="notification-close" 
            onClick={() => setNotification({ show: false, message: '', type: 'success' })}
          >
            <i className="material-icons">close</i>
          </button>
        </div>
      )}

      {/* Course Detail Modal */}
      {courseDetailModal.show && (
        <div className="modal-overlay" onClick={closeCourseDetailModal}>
          <div className="modal-content course-detail-modal" onClick={(e) => e.stopPropagation()}>
            <span className="modal-close" onClick={closeCourseDetailModal}>&times;</span>
            
            <div className="course-preview">
              <div className="course-image">
                <img src={courseDetailModal.course?.image} alt={courseDetailModal.course?.title} />
                <div className="play-button">
                  <i className="material-icons">play_arrow</i>
                </div>
              </div>
              <div className="course-info">
                <div className="course-meta">
                  <span className="course-level">{courseDetailModal.course?.level}</span>
                  <span className="course-duration">{courseDetailModal.course?.duration}</span>
                  <span className="course-students">{courseDetailModal.course?.students.toLocaleString()} students</span>
                </div>
                <div className="course-rating">
                  <div className="stars">
                    {generateStars(courseDetailModal.course?.rating || 0)}
                  </div>
                  <span className="rating-text">{courseDetailModal.course?.rating} ({courseDetailModal.course?.reviewCount} reviews)</span>
                </div>
                <div className="course-price">
                  <span className="price">${courseDetailModal.course?.price}</span>
                  {courseDetailModal.course?.originalPrice && courseDetailModal.course?.originalPrice > courseDetailModal.course?.price && (
                    <span className="original-price">${courseDetailModal.course?.originalPrice}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="course-description">
              <h3>About this course</h3>
              <p>{courseDetailModal.course?.description}</p>
            </div>

            <div className="course-curriculum">
              <h3>What you'll learn</h3>
              <ul>
                {courseDetailModal.course?.learningObjectives?.map((objective, index) => (
                  <li key={index}>{objective}</li>
                ))}
              </ul>
            </div>

            <div className="course-instructor">
              <h3>Instructor</h3>
              <div className="instructor-info">
                <img src="https://via.placeholder.com/60x60/667eea/ffffff?text=SI" alt={courseDetailModal.course?.instructor} />
                <div className="instructor-details">
                  <h4>{courseDetailModal.course?.instructor}</h4>
                  <p>Expert Instructor</p>
                  <p>{courseDetailModal.course?.instructorBio}</p>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="teacher-course-btn" onClick={() => showNotification('Added to wishlist', 'success')}>
                <i className="material-icons">favorite_border</i>
                Save for Later
              </button>
              <button className="teacher-course-btn primary" onClick={() => showNotification('Course enrolled successfully', 'success')}>
                <i className="material-icons">school</i>
                Enroll Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Free Talk Room Modal */}
      {roomModal.show && (
        <div className="modal-overlay" onClick={leaveRoom}>
          <div className="modal-content free-talk-room-modal" onClick={(e) => e.stopPropagation()}>
            <span className="modal-close" onClick={leaveRoom}>&times;</span>
            
            <div className="room-header">
              <h2>{roomModal.room.title}</h2>
              <div className="room-meta">
                <span>🌍 {roomModal.room.language}</span>
                <span>🎧 {roomModal.room.type.charAt(0).toUpperCase() + roomModal.room.type.slice(1)}</span>
                <span>📚 {roomModal.room.level.charAt(0).toUpperCase() + roomModal.room.level.slice(1)}</span>
                <span className={`status-badge ${roomModal.room.status}`}>{roomModal.room.status}</span>
              </div>
              {roomModal.room.isPrivate && (
                <div className="invite-code">
                  <h4>Invite Code:</h4>
                  <p>XXXX-XXXX-XXXX</p>
                </div>
              )}
            </div>

            <div className="room-participants">
              <h3>Participants ({participants.length})</h3>
              <div className="participants-list">
                {participants.map(participant => (
                  <div key={participant.id} className="participant-card">
                    <div className="participant-avatar">{participant.avatar}</div>
                    <div className="participant-info">
                      <h4>{participant.name}</h4>
                      <p>{participant.country}</p>
                    </div>
                    <div className="participant-status">
                      <span className={`status-badge ${participant.isMuted ? 'muted' : ''}`}>
                        {participant.isMuted ? 'Muted' : 'Active'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="room-chat">
              <div className="chat-header">
                <h3>Chat</h3>
                <div className="chat-controls">
                  <button onClick={toggleMic} className={`control-btn ${isMicOn ? 'active' : ''}`}>
                    <i className="material-icons">mic</i>
                  </button>
                  <button onClick={toggleSpeaker} className={`control-btn ${isSpeakerOn ? 'active' : ''}`}>
                    <i className="material-icons">volume_up</i>
                  </button>
                  <button onClick={shareScreen} className="control-btn">
                    <i className="material-icons">screen_share</i>
                  </button>
                </div>
              </div>
              <div className="chat-messages">
                {chatMessages.map(msg => (
                  <div key={msg.id} className={`message ${msg.isOwn ? 'own-message' : ''}`}>
                    <div className="message-author">{msg.author}</div>
                    <div className="message-content">{msg.content}</div>
                    <div className="message-time">{msg.time}</div>
                  </div>
                ))}
              </div>
              <div className="chat-input-container">
                <input 
                  type="text" 
                  placeholder="Type a message..." 
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleMessageKeyPress}
                />
                <button onClick={sendMessage} className="send-btn">
                  <i className="material-icons">send</i>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report User Modal */}
      {reportModal.show && (
        <div className="modal-overlay" onClick={closeReportModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <span className="close" onClick={closeReportModal}>&times;</span>
            <h2>Report {reportModal.target?.type === 'user' ? 'User' : 'Room'}</h2>
            <form onSubmit={handleReportSubmit}>
              <div className="form-group">
                <label htmlFor="reportReason" className="required">Reason for Report *</label>
                <select 
                  id="reportReason"
                  value={reportForm.reason}
                  onChange={(e) => setReportForm(prev => ({ ...prev, reason: e.target.value }))}
                  required
                >
                  <option value="">Select a reason</option>
                  <option value="inappropriate">Inappropriate behavior</option>
                  <option value="spam">Spam or advertising</option>
                  <option value="harassment">Harassment or bullying</option>
                  <option value="offensive">Offensive language</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="reportDescription">Description</label>
                <textarea 
                  id="reportDescription"
                  value={reportForm.description}
                  onChange={(e) => setReportForm(prev => ({ ...prev, description: e.target.value }))}
                  rows="4" 
                  placeholder="Please provide details about the incident..."
                ></textarea>
              </div>
              <div className="form-actions">
                <button type="submit" className="form-btn primary">
                  <i className="material-icons">report</i>
                  Submit Report
                </button>
                <button type="button" className="form-btn secondary" onClick={closeReportModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Support Classes Modal */}
      {supportClassModal.show && (
        <div className="modal-overlay" onClick={closeSupportClassModal}>
          <div className="modal-content support-class-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <i className="material-icons">
                  {supportClassModal.type === 'recorded' ? 'play_circle' : 
                   supportClassModal.type === 'workshops' ? 'live_tv' : 'class'}
                </i>
                {supportClassModal.type === 'recorded' ? 'Recorded Courses' :
                 supportClassModal.type === 'workshops' ? 'Workshops' : 'Normal Classes'}
              </h2>
              <span className="close" onClick={closeSupportClassModal}>&times;</span>
            </div>
            
            <div className="modal-body">
              <div className="support-class-steps">
                <div className="step-indicator">
                  <div className="step active">1</div>
                  <div className="step-line"></div>
                  <div className={`step ${selectedSchoolLevel ? 'active' : ''}`}>2</div>
                  <div className="step-line"></div>
                  <div className={`step ${selectedSubject && selectedYear ? 'active' : ''}`}>3</div>
                </div>
                
                <div className="step-labels">
                  <span>{t('supportClasses.schoolLevel')}</span>
                  <span>{t('supportClasses.subject')}</span>
                  <span>{t('supportClasses.year')}</span>
                </div>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleSupportClassSubmit(); }}>
                {/* Step 1: School Level Selection */}
                <div className="form-group">
                  <label htmlFor="schoolLevel" className="required">{t('supportClasses.schoolLevel')} *</label>
                  <select 
                    id="schoolLevel"
                    value={selectedSchoolLevel}
                    onChange={(e) => {
                      setSelectedSchoolLevel(e.target.value);
                      setSelectedSubject('');
                      setSelectedYear('');
                    }}
                    required
                  >
                    <option value="">{t('supportClasses.selectSchoolLevel')}</option>
                    {getSchoolLevels().map(level => (
                      <option key={level.value} value={level.value}>{level.label}</option>
                    ))}
                  </select>
                </div>

                {/* Step 2: Subject Selection */}
                {selectedSchoolLevel && (
                  <div className="form-group">
                    <label htmlFor="subject" className="required">{t('supportClasses.subject')} *</label>
                    <select 
                      id="subject"
                      value={selectedSubject}
                      onChange={(e) => {
                        setSelectedSubject(e.target.value);
                        setSelectedYear('');
                      }}
                      required
                    >
                      <option value="">{t('supportClasses.selectSubject')}</option>
                      {getSubjectsByLevel(selectedSchoolLevel).map(subject => (
                        <option key={subject} value={subject}>{subject}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Step 3: Year Selection */}
                {selectedSubject && (
                  <div className="form-group">
                    <label htmlFor="year" className="required">{t('supportClasses.year')} *</label>
                    <select 
                      id="year"
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                      required
                    >
                      <option value="">{t('supportClasses.selectYear')}</option>
                      {getSchoolLevels().find(level => level.value === selectedSchoolLevel)?.years.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-actions">
                  <button type="button" className="form-btn secondary" onClick={closeSupportClassModal}>
                    {t('supportClasses.cancel')}
                  </button>
                  <button 
                    type="submit" 
                    className={`form-btn primary ${supportClassModal.type}`}
                    disabled={!selectedSchoolLevel || !selectedSubject || !selectedYear}
                  >
                    <i className="material-icons">play_arrow</i>
                    {t('supportClasses.startLearning')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherDashboard; 